<template>
  <div
    class="tw:flex tw:flex-col tw:gap-px"
    :class="{ 'tw:p-4': !isMobile, 'tw:p-2': isMobile }"
  >
    <div class="tw:flex tw:flex-col tw:p-2">
      <Checkbox
        v-model="selectAll"
        data-testid="select-all"
        dense
        :indeterminate="selectAllIndeterminate"
        :label="getLocaleText('global.select_all')"
        new-design
      />
    </div>
    <div :class="{ 'tw:columns-2': isMobile }">
      <div
        v-for="enabledMedium in enabledMedia"
        :key="enabledMedium.field"
        class="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:p-2 tw:hover:bg-denim-100"
        :class="{
          'tw:bg-denim-100':
            selectedMedium && selectedMedium.field === enabledMedium.field,
        }"
        :data-testid="`medium-${enabledMedium.field}`"
        @click="selectMedium(enabledMedium)"
      >
        <div class="tw:flex tw:items-center">
          <Checkbox
            v-model="selectedMedia"
            :color="enabledMedium.field"
            dense
            new-design
            :value="enabledMedium.medium"
            @click.stop
            @update:model-value="emit('toggle-medium', enabledMedium, $event)"
          />
        </div>
        <div class="tw:w-full">
          {{ enabledMedium.label }}
        </div>
        <div
          v-if="
            selectedMedia.includes(enabledMedium.medium) &&
            selectedMedium &&
            selectedMedium.field === enabledMedium.field
          "
          class="tw:items-centers tw:flex"
        >
          <BaseIcon
            class="tw:text-lg"
            icon="filter"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseIcon from "shared/components/base/BaseIcon.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { type Medium, type MediumName, getSources } from "shared/helpers/media";
import { type Nullable, type Stream } from "shared/types";

interface StreamFiltersMediaProps {
  stream: Stream;
}

const isMobile = inject<boolean>("isMobile");

const props = defineProps<StreamFiltersMediaProps>();

const emit = defineEmits<{
  "toggle-medium": [Medium, MediumName[]];
}>();

const media = defineModel<MediumName[]>("media", {
  default: () => [],
});

const refreshMedia = defineModel<boolean>("refreshMedia");

const selectedMedium = defineModel<Nullable<Medium>>("medium", {
  default: null,
});

const enabledMedia = computed<Medium[]>(() =>
  getSources().filter(({ medium }) =>
    props.stream.enabled_media.includes(medium)
  )
);

const selectedMedia = ref<MediumName[]>(
  media.value.length
    ? media.value
    : enabledMedia.value.map((medium) => medium.medium)
);

const selectAll = computed({
  get() {
    return enabledMedia.value.length === selectedMedia.value.length;
  },
  set(value) {
    if (value) {
      selectedMedia.value = enabledMedia.value.map((medium) => medium.medium);
      if (!selectedMedium.value) selectFirstMedium();
    } else {
      selectedMedia.value = [];
      selectedMedium.value = null;
    }
  },
});

const selectAllIndeterminate = computed(() =>
  Boolean(
    media.value.length && enabledMedia.value.length !== media.value.length
  )
);

function selectMedium(medium: Medium) {
  if (
    media.value.includes(medium.medium) &&
    selectedMedium.value?.medium === medium.medium
  ) {
    selectedMedium.value = null;

    selectedMedia.value = media.value.filter(
      (mediaType) => mediaType !== medium.medium
    );

    return;
  }

  if (!media.value.includes(medium.medium)) {
    selectedMedia.value = media.value.concat(medium.medium);
  }

  selectedMedium.value = medium;
}

function selectFirstMedium() {
  const firstSelectedMedium = selectedMedia.value.at(0);

  if (firstSelectedMedium) {
    selectedMedium.value =
      enabledMedia.value.find(
        (enabledMedium) => enabledMedium.medium === firstSelectedMedium
      ) ?? null;
  }
}

onMounted(() => {
  selectFirstMedium();
});

watch(refreshMedia, () => {
  if (refreshMedia.value) {
    refreshMedia.value = false;

    selectedMedia.value = media.value.length
      ? media.value
      : enabledMedia.value.map((medium) => medium.medium);
  }
});

watch(
  selectedMedia,
  () => {
    media.value = selectedMedia.value;
  },
  { immediate: true }
);
</script>
